import i18n from '@/i18n';

const STORAGE_KEY = 'mgr_sso_pkce';
const RETURN_TO_ROUTES = {
    home: 'home',
    login: 'login',
    signup: 'signup',
};

function t(key) {
    return i18n.t(key);
}

function base64UrlEncode(bytes) {
    let str = '';
    const arr = new Uint8Array(bytes);
    for (let i = 0; i < arr.length; i++) {
        str += String.fromCharCode(arr[i]);
    }
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlEncodeJson(value) {
    return btoa(JSON.stringify(value))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

function base64UrlDecodeJson(value) {
    const padded = value.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
    return JSON.parse(atob(padded + pad));
}

function randomString(byteLength = 32) {
    return base64UrlEncode(crypto.getRandomValues(new Uint8Array(byteLength)));
}

async function sha256(value) {
    const data = new TextEncoder().encode(value);
    return crypto.subtle.digest('SHA-256', data);
}

async function createPkce() {
    const verifier = randomString(32);
    const challenge = base64UrlEncode(await sha256(verifier));
    return { challenge, verifier };
}

function redirectUri() {
    if (process.env.VUE_APP_SSO_REDIRECT_URI) {
        return process.env.VUE_APP_SSO_REDIRECT_URI;
    }
    return `${(process.env.VUE_APP_API_URL || '').replace(/\/$/, '')}/auth/sso/callback`;
}

function normalizeIssuer(issuer) {
    let value = (issuer || '').trim();
    if (!value) {
        return value;
    }
    if (!/^https?:\/\//i.test(value)) {
        value = `https://${value}`;
    }
    return value.replace(/\/$/, '');
}

async function fetchDiscovery(issuer) {
    const url = `${normalizeIssuer(issuer)}/.well-known/openid-configuration`;
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(t('messages.sso_openid_config_failed'));
    }
    return response.json();
}

function routeNameForReturnTo(returnTo) {
    return RETURN_TO_ROUTES[returnTo] || 'home';
}

function returnToFromState(state) {
    if (!state || typeof state !== 'string') {
        return null;
    }
    try {
        const payload = base64UrlDecodeJson(state);
        return payload?.r || null;
    } catch (e) {
        return null;
    }
}

export default {

    async handleCallback(search) {
        const query = new URLSearchParams(search);
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
        const fail = (message) => {
            sessionStorage.removeItem(STORAGE_KEY);
            throw new Error(message);
        };

        const error = query.get('error');
        if (error) {
            fail(query.get('error_description') || error);
        }

        const code = query.get('code');
        const state = query.get('state');
        sessionStorage.removeItem(STORAGE_KEY);

        if (!code || !stored || !stored.verifier || !state) {
            fail(t('messages.sso_invalid_callback'));
        }

        let statePayload;
        try {
            statePayload = base64UrlDecodeJson(state);
        } catch (e) {
            fail(t('messages.sso_invalid_callback'));
        }
        if (!statePayload || statePayload.n !== stored.nonce) {
            fail(t('messages.sso_invalid_callback'));
        }
        if (!stored.tokenEndpoint) {
            fail(t('messages.sso_openid_config_failed'));
        }

        const body = new URLSearchParams({
            client_id: stored.clientId,
            code,
            code_verifier: stored.verifier,
            grant_type: 'authorization_code',
            redirect_uri: redirectUri(),
        });

        const response = await fetch(stored.tokenEndpoint, {
            body,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            method: 'POST',
        });

        if (!response.ok) {
            fail(t('messages.sso_token_exchange_failed'));
        }

        const tokens = await response.json();
        if (!tokens.id_token) {
            fail(t('messages.sso_token_exchange_failed'));
        }

        return {
            id_token: tokens.id_token,
            nonce: stored.nonce,
        };
    },

    redirectUri,

    returnToFromState,

    routeNameForReturnTo,

    async startLogin({ agencyUuid, clientId, issuer, returnTo = 'login' }) {
        if (!agencyUuid || !clientId || !issuer) {
            throw new Error(t('messages.sso_not_configured'));
        }

        const discovery = await fetchDiscovery(issuer);
        if (!discovery.authorization_endpoint) {
            throw new Error(t('messages.sso_openid_config_failed'));
        }

        const { challenge, verifier } = await createPkce();
        const nonce = randomString(16);
        const state = base64UrlEncodeJson({
            a: agencyUuid,
            n: nonce,
            r: returnTo,
        });
        const normalizedIssuer = normalizeIssuer(issuer);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            clientId,
            issuer: normalizedIssuer,
            nonce,
            returnTo,
            tokenEndpoint: discovery.token_endpoint,
            verifier,
        }));

        const params = new URLSearchParams({
            client_id: clientId,
            code_challenge: challenge,
            code_challenge_method: 'S256',
            nonce,
            redirect_uri: redirectUri(),
            response_type: 'code',
            scope: 'openid email profile',
            state,
        });

        window.location.assign(`${discovery.authorization_endpoint}?${params.toString()}`);
    },

};
